import * as React from "react";

import { createStyles, withStyles, WithStyles } from "@material-ui/core/styles";
import classnames from "classnames";
import styled from "styled-components";

import { RelodeTheme } from "../";
import { ColorType } from "../utils";

const styles = (theme: RelodeTheme) =>
    createStyles({
        root: {
            position: "relative",
            display: "inline-flex",
            flexDirection: "column-reverse",
            alignItems: "flex-start",
            width: "auto",
            height: "auto",
            outline: "none",
        },
        cardHeader: {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            boxShadow: theme.shadows[4],
            paddingTop: 0,
            paddingBottom: theme.spacing(3),
            width: "auto",
            display: "flex",
            minWidth: 200,
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
        },
        content: {
            padding: "16px 16px 16px 16px",
            boxShadow: theme.shadows[2],
            height: "100%",
            width: "100%",
            minWidth: "auto",
            alignSelf: "stretch",
            borderBottomLeftRadius: 2,
            borderBottomRightRadius: 2,
            backgroundColor: theme.palette.background.paper,
        },
        noPaper: {
            boxShadow: "none",
            backgroundColor: "inherit",
        },
        primary: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
        },
        secondary: {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
        },
        danger: {
            backgroundColor: theme.relode.colors.danger.main,
            color: theme.relode.colors.danger.contrastText,
        },
        flat: {
            backgroundColor: theme.relode.colors.flat.main,
            color: theme.relode.colors.flat.contrastText,
        },
        inherit: {},
        default: {},
        left: {
            alignSelf: "flex-start",
        },
        right: {
            alignSelf: "flex-end",
        },
        center: {
            alignSelf: "center",
        },
        full: {
            alignSelf: "stretch",
        },
        rounded: {
            borderRadius: 8,
        },
        fullSize: {
            width: "100%",
            height: "100%",
        },
    });

const ActionsWrapper = styled.div`
    position: absolute;
    bottom: 24px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
`;

interface CardWithHeaderProps extends WithStyles<typeof styles> {
    color: ColorType;
    header: JSX.Element;
    actions?: JSX.Element;
    style?: any;
    className?: string;
    align?: "left" | "center" | "right" | "full";
    rounded?: boolean;
    customClasses?: any;
    children?: any;
    noPaper?: boolean;
    fullSize?: boolean;
}

export const CardWithHeader = withStyles(styles, { name: "CardWithHeader" })((props: CardWithHeaderProps) => {
    const { classes, header, style, className, color, align = "left", rounded, noPaper, actions, fullSize } = props;

    const baseClassNames = classnames(
        {
            [classes.root]: true,
            [classes.fullSize]: !!fullSize,
        },
        className
    );

    const headerClassNames = classnames({
        [classes.cardHeader]: true,
        [classes[color]]: color,
        [classes[align]]: true,
        [classes.rounded]: rounded,
    });

    const cardClassNames = classnames({
        [classes.content]: true,
        [classes.rounded]: rounded,
        [classes.noPaper]: !!noPaper,
    });

    return (
        <div className={baseClassNames} style={style}>
            <div className={cardClassNames}>
                {props.children}
                {actions && <ActionsWrapper>{actions}</ActionsWrapper>}
            </div>
            <div className={headerClassNames}>{header}</div>
        </div>
    );
});
